import { useState, type FormEvent } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useCredentialSave } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { CredentialHealth, CredentialShell, SaveFeedback } from "./CredentialShell";
import { Field } from "./Field";

const ERRORS: Record<string, string> = {
  invalid_amazon_api_version: "Escolha a versão exibida no portal (2.1 ou 3.1).",
  invalid_amazon_credential_id: "ID da credencial inválido.",
  invalid_amazon_tag: "Partner tag inválida (ex.: seunome-20).",
  invalid_amazon_secret: "Segredo inválido (mínimo 8 caracteres, sem espaços).",
  amazon_secret_required: "Informe o segredo da credencial.",
  tenant_not_found: "Conta não encontrada. Entre de novo no painel.",
};

const SELECT_CLASS =
  "h-10 w-full rounded-lg border border-[hsl(var(--border-strong))] bg-background/60 px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface Props {
  readonly credential: AffiliateCredentialPublic | undefined;
}

/** Amazon via Creators API (versao, credential id, segredo no Vault, partner tag). */
export function AmazonCredentialSection({ credential }: Props): JSX.Element {
  const config = credential?.config_public ?? {};
  return (
    <CredentialShell id="amazon" title="Amazon">
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-foreground">Creators API (oficial)</p>
        {credential && (
          <p className="font-mono text-xs text-muted-foreground">
            Tag {credential.tag_masked ?? "-"} · versão {String(config["apiVersion"] ?? "-")} · ID{" "}
            {String(config["credentialIdMasked"] ?? "-")}
          </p>
        )}
        <p className="text-xs text-[hsl(var(--gold))]">
          Credencial nova pode levar até 48h para ser liberada pela Amazon. Contas sem 10 vendas qualificadas nos
          últimos 30 dias recebem erro de elegibilidade (AssociateNotEligible); o link com sua tag continua saindo.
        </p>
      </div>
      <CredentialHealth credential={credential} />
      <AmazonCreatorsForm
        initialVersion={typeof config["apiVersion"] === "string" ? config["apiVersion"] : "3.1"}
        hasSecret={config["hasSecret"] === true}
      />
      <AmazonTagOnlyForm />
    </CredentialShell>
  );
}

function AmazonCreatorsForm({ initialVersion, hasSecret }: { initialVersion: string; hasSecret: boolean }): JSX.Element {
  const [version, setVersion] = useState(initialVersion);
  const [credentialId, setCredentialId] = useState("");
  const [secret, setSecret] = useState("");
  const [partnerTag, setPartnerTag] = useState("");
  const { saving, status, error, save } = useCredentialSave("amazon", ERRORS);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const params = { p_api_version: version, p_credential_id: credentialId.trim(), p_credential_secret: secret.trim(), p_partner_tag: partnerTag.trim() };
    if (await save("upsert_amazon_creators_credential", params)) {
      setSecret("");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="amazon-version" variant="field">Versão da credencial</Label>
          <select id="amazon-version" className={SELECT_CLASS} value={version} onChange={(e) => setVersion(e.target.value)}>
            <option value="3.1">3.1</option>
            <option value="2.1">2.1</option>
          </select>
        </div>
        <Field id="amazon-partner-tag" label="Partner tag" value={partnerTag} onValue={setPartnerTag} placeholder="seunome-20" required />
        <Field id="amazon-credential-id" label="ID da credencial" value={credentialId} onValue={setCredentialId} placeholder="Credential ID" required />
        <Field id="amazon-secret" label="Segredo" type="password" value={secret} onValue={setSecret}
          placeholder={hasSecret ? "Deixe em branco para manter o atual" : "Credential Secret"} />
        <div className="flex justify-end sm:col-span-2">
          <Button type="submit" variant="gold" disabled={saving}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            Salvar e testar
          </Button>
        </div>
      </form>
      <SaveFeedback status={status} error={error} />
    </>
  );
}

/** Alternativa sem Creators API: so a tag (link direto amazon.com.br/dp/ASIN?tag=). */
function AmazonTagOnlyForm(): JSX.Element {
  const [tag, setTag] = useState("");
  const { saving, status, error, save } = useCredentialSave("amazon", ERRORS);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (await save("upsert_amazon_affiliate_tag", { p_tag: tag.trim() })) {
      setTag("");
    }
  };

  return (
    <details className="rounded-lg border border-border px-4 py-3">
      <summary className="cursor-pointer text-sm font-medium text-foreground">Ainda não tenho a Creators API (só a tag)</summary>
      <form onSubmit={handleSubmit} className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field id="amazon-tag-only" label="Tag Amazon" value={tag} onValue={setTag} placeholder="seunome-20" required />
        <Button type="submit" variant="outline" disabled={saving}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
          Salvar tag
        </Button>
      </form>
      <SaveFeedback status={status} error={error} />
    </details>
  );
}
